import React, {createRef} from 'react'
import {findDOMNode} from "react-dom"

import DndPreviewLayerComponent_V2, {
    DndPreviewLayerComponent_State
} from "../../../../../core/gd-modules/dnd/preview-layer/dnd-preview-layer-component-v2"
import {DNDPreviewLayerProps} from "../../../../../core/gd-modules/dnd/preview-layer/dnd-preview-layer-component"
import ProjectIconUI5 from "../../../../../core/gd-components/icons/project/project-icon-ui5"
import ProjectSession from "../../../../../core/model2/project/project-session"
import {
    MENU_PROJECTS_DND_DRAGGED_OUT,
    MENU_PROJECTS_DND_HOVER,
    MenuProjectsDndHoverParams
} from "./@events"

type State = {
    target?     : ProjectSession    // the project that will actually become the dragged item's parent, if shown
    hoverRowId? : ID                // the row whose hover produced target - see onDraggedOut
} & DndPreviewLayerComponent_State

// const debug = "MenuProjects_DndPreviewLayer"
const debug = false

// icon + name, exactly as the tasks-view table pill renders a project row
// (tasks-view/table/components/dnd/preview-layer.tsx, _getItemIcon + the name div)
function renderProject(project: ProjectSession) {
    return (
        <>
            <ProjectIconUI5
                className   = "preview-icon m-r-8"
                project     = {project}
            />

            <div className="ui5-txt-default max-w-150 txt-truncate ui5-txt-folder">
                {project.name}
            </div>
        </>
    )
}

// The tasks-view table's drag pill (tasks-view/table/components/dnd/preview-layer.tsx), 25% narrower.
// Positioning is ported from sortable-list-4/preview-layer - this tree's own prior reorder preview:
// pin the pill to the row's rect at drag start and shift it by the cumulative mouse delta, so the
// cursor stays over the exact point it grabbed instead of re-centering on the pill every frame.
export default class MenuProjects_DndPreviewLayer extends DndPreviewLayerComponent_V2<DNDPreviewLayerProps, State> {

    private previewRef: any = createRef()

    componentDidMount() {
        super.componentDidMount()

        gd.bus.subscribe(MENU_PROJECTS_DND_HOVER, this, this.onHover)
        gd.bus.subscribe(MENU_PROJECTS_DND_DRAGGED_OUT, this, this.onDraggedOut)
    }

    componentWillUnmount() {
        super.componentWillUnmount()

        gd.bus.unsubscribe(this)
    }

    // only the target half depends on hover state - position is handled imperatively in
    // updatePreview, so a plain setState (no forced re-measure) is enough here
    onHover = (data: MenuProjectsDndHoverParams) => {
        debug && console.log(debug, 'onHover', data.project?.name, data.target?.name, data.showTarget)

        this.setState({
            hoverRowId  : data.project?.id,
            target      : data.showTarget ? data.target : undefined,
        })
    }

    // both rows on a shared drop rect edge are dragged over at once (drop-rect.ts) - only the row that
    // produced the shown target may clear it, or the other row's exit blanks the arrow with the cursor
    // still over it. Matched on the hovered row's id, not the (possibly unrelated) target's id, since a
    // reorder's target is the hovered rows' shared parent rather than either row itself
    onDraggedOut = (data: MenuProjectsDndHoverParams) => {
        if (data.project.id !== this.state.hoverRowId) {
            return
        }

        this.setState({
            target      : undefined,
            hoverRowId  : undefined,
        })
    }

    showPreview(source: any, mouseEvent: any) {
        this.initialPreviewSourceRect = source.getInitialRect()

        this.setState({
            source: source
        }, () => {
            this.previewNode = findDOMNode(this.previewRef.current)

            // detect if the layer is displaced on the screen and compensate (may be caused by a
            // parent 'transform' css property, which changes what a fixed-position child is
            // relative to) - ported from sortable-list-4/preview-layer/reorder-preview-layer.tsx
            const node: any = findDOMNode(this)
            const rect = node.getBoundingClientRect()
            node.style.setProperty('left', "{0}px".f(-rect.x))
            node.style.setProperty('top', "{0}px".f(-rect.y))
            node.style.setProperty('width', "{0}px".f(window.innerWidth))
            node.style.setProperty('height', "{0}px".f(window.innerHeight))

            this.updatePreview()
        })
    }

    hidePreview() {
        this.initialPreviewSourceRect = null
        this.previewNode = null

        this.setState({
            source      : null,
            target      : undefined,
            hoverRowId  : undefined,
        })
    }

    updatePreview(dx = 0, dy = 0) {
        if (!this.initialPreviewSourceRect || !this.previewNode) {
            return
        }

        const rect  = this.initialPreviewSourceRect
        const top   = rect.top + (rect.height - this.previewNode.offsetHeight) / 2  // 32px pill centred on the 30px row

        this.previewNode.style.setProperty("left", (rect.left + dx) + "px")
        this.previewNode.style.setProperty("top", (top + dy) + "px")
    }

    render() {
        if (!this.state.source) {
            return null
        }

        return (
            <div className="menu-projects-preview-layer">
                <div ref={this.previewRef} className="drag-preview">

                    {renderProject(this.state.source.props.project)}

                    {this.state.target &&
                        <>
                            <div className="right-icon i5-long-arrow-right m-r-5 m-l-5"/>

                            {renderProject(this.state.target)}
                        </>
                    }
                </div>
            </div>
        )
    }
}
